"use client"

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import SvgIcon from '@components/shared/svg-icons/SvgIcon'
import { useUnread } from '@components/messages/unread'
import { accountLinks, isFirm } from '@components/session/UserNav'
import salonLogo from '@utils/links/salonLogo'
import mix from '@utils/styles/mix'

import styles from './AccountShell.module.css'

const DOMAIN = process.env.NEXT_PUBLIC_DOMAIN
const PRIVATE_DEFAULT_NAME = 'Konto prywatne'

const plural = (n, a, b, c) => {
    const t = n % 10, h = n % 100
    return n === 1 ? a : (t >= 2 && t <= 4 && (h < 12 || h > 14)) ? b : c
}
const money = (n) => (Number(n) || 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' zł'

// Nazwa konta z „Moich danych”; bez niej — nazwa firmy albo imię i nazwisko, na końcu e-mail (kolejność jak przy ogłoszeniach).
function accountName (user) {
    if (user.alias) return user.alias
    if (isFirm(user) && user.dealer.firm_name) return user.dealer.firm_name
    const full = [user.firstname, user.lastname].filter(Boolean).join(' ')
    return (full && full !== PRIVATE_DEFAULT_NAME ? full : '') || user.contact?.email || ''
}

function accountKind (user) {
    if (user.is_partner) return 'Konto partnera'
    return isFirm(user) ? 'Konto firmowe' : 'Konto prywatne'
}

function initials (name) {
    return name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('')
}

function linkMeta (key, user, summary) {
    switch (key) {
        case 'ogloszenia':
            if (!summary) return ''
            if (!summary.active && !summary.pending) return 'Dodaj pierwsze ogłoszenie'
            return `${summary.active} ${plural(summary.active, 'aktywne', 'aktywne', 'aktywnych')}` + (summary.pending ? ` · ${summary.pending} do opłacenia` : '')
        case 'kupno':
            return `${summary.buy} ${plural(summary.buy, 'ogłoszenie „Kupię”', 'ogłoszenia „Kupię”', 'ogłoszeń „Kupię”')}`
        case 'portfel':
            return `Saldo: ${money(user.wallet)}`
        case 'wizytowka':
            return user.dealer?.subdomain ? `${user.dealer.subdomain}.${DOMAIN}` : 'Załóż wizytówkę firmy'
        case 'dane':
            return isFirm(user) ? 'Firma, hasło, faktury, integracje, DAT' : 'Dane, hasło, faktury, DAT'
        case 'obserwowane':
            return 'Ogłoszenia i wyszukiwania'
        case 'wiadomosci':
            return 'Rozmowy i wiadomości od Samochody.pl'
        default:
            return ''
    }
}

const CURRENT = {
    ogloszenia: '/profil/moje-ogloszenia',
    kupno: '/profil/moje-oferty-kupna',
    portfel: '/profil/moj-portfel',
    wizytowka: '/profil/dealer',
    dane: '/profil/moje-dane',
    obserwowane: '/profil/obserwowane',
    produkty: '/profil/partner/moje-produkty',
    artykuly: '/profil/partner/moje-artykuly',
}

export function AccountCard ({ user }) {
    const name = accountName(user)
    return (
        <div className={styles.who}>
            { user.has_logo
                ? <span aria-hidden="true" className={mix([styles.ava, styles.avaLogo])}><img alt="" height="48" src={salonLogo(user._id)} width="72" /></span>
                : <span aria-hidden="true" className={styles.ava}>{initials(name)}</span> }
            <div>
                <b>{name}</b>
                <small>{accountKind(user)}</small>
            </div>
        </div>
    )
}

// Sekcje konta z opisem; „N do opłacenia” przy „Moich ogłoszeniach”, liczba nieprzeczytanych przy „Wiadomościach”.
// brief (menu boczne): z opisów tylko saldo przy „Moim portfelu”.
export default function AccountNav ({ user, summary, label = 'Sekcje konta', brief }) {
    const path = usePathname() || ''
    const { total: unread } = useUnread()
    const links = accountLinks(user, summary)

    return (
        <nav aria-label={label} className={styles.nav}>
            <ul>
                { links.map((link, i) => {
                    const sep = i > 0 && link.group !== links[i - 1].group
                    const current = CURRENT[link.key] && path.startsWith(CURRENT[link.key])
                    const warn = link.key === 'ogloszenia' && summary?.pending
                    const messages = link.key === 'wiadomosci' ? unread : 0
                    const meta = !brief || link.key === 'portfel' ? linkMeta(link.key, user, summary) : ''
                    const inner = <>
                        <SvgIcon name={link.icon} />
                        <span>{link.t}{ meta && <small>{meta}</small> }</span>
                        { warn
                            ? <span aria-hidden="true" className={styles.badge}>{summary.pending}</span>
                            : messages
                                ? <><span aria-hidden="true" className={mix([styles.badge, styles.badgeUnread])}>{messages}</span><span className={styles.sr}>, nieprzeczytane: {messages}</span></>
                                : <SvgIcon className={styles.chev} name="chev" size="sm" /> }
                    </>
                    return (
                        <li className={sep ? styles.sep : undefined} key={link.key}>
                            <Link aria-current={current ? 'page' : undefined} className={styles.item} href={link.href} prefetch={false}>{inner}</Link>
                        </li>
                    )
                }) }
            </ul>
        </nav>
    )
}
